import { Clipboard } from '@angular/cdk/clipboard';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    QueryList,
    SimpleChanges,
    ViewChild,
    ViewChildren,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Select, Store } from '@ngxs/store';
import { TableExportDirective } from 'src/app/shared/table-export/table-export.directive';
import { NGXLogger } from 'ngx-logger';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subscription, take } from 'rxjs';
import { BudgetParityService } from 'src/app/budget/budget-parity.service';
import { BudgetService } from 'src/app/budget/budget.service';
import { ComanageService } from 'src/app/comanage/comanage.service';
import { EmployeeService } from 'src/app/employees/employees.service';
import { Employee } from 'src/app/models/Employee';
import EmployeeWithBudgets from 'src/app/models/EmployeeWithBudgets';
import { EmployeeWithCosts } from 'src/app/models/EmployeeWithCosts';
import Project from 'src/app/models/Project';
import { TIMESHEET_STATUS, Timesheet } from 'src/app/models/Timesheet';
import { Timeslot } from 'src/app/models/Timeslot';
import { ConfirmationModalComponent } from 'src/app/shared/confirmation-modal/confirmation-modal.component';
import { MinutesToMinutesAndHoursPipe } from 'src/app/shared/pipes/minutes-to-minutes-and-hours.pipe';

import { EMOJIS } from '../../models/EMOJIS';
import { AddTimesheetModalComponent } from '../add-timesheet-modal/add-timesheet-modal.component';
import { CronjobRulesSettingsModalComponent } from '../cronjob-rules-settings-modal/cronjob-rules-settings-modal.component';
import { InvoiceDialogComponent } from '../invoice-dialog/invoice-dialog.component';
import { LinkInvoiceDialogComponent } from '../link-invoice-dialog/link-invoice-dialog.component';
import { NoInvoiceDialogComponent } from '../no-invoice-dialog/no-invoice-dialog.component';
import { ReportsModalComponent } from '../reports-modal/reports-modal.component';
import { TimesheetInfoModalComponent } from '../timesheet-info-modal/timesheet-info-modal.component';
import { ChangeTimesheetStatus, ClearNoInvoice, RemoveTimesheet, SetNoInvoice, SetTimeslotToIsBilled, UnbillTimeslot } from '../timesheets.actions';
import { TimesheetsService } from '../timesheets.service';
import { TimesheetState } from '../timesheets.state';

interface ReadyToInvoiceStage {
    key: string;
    label: string;
    passed: boolean;
    reason: string;
    details: string[];
}

@Component({
    selector: 'app-timesheets-project-list',
    templateUrl: './timesheets-project-list.component.html',
    styleUrls: ['./timesheets-project-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class TimesheetsProjectListComponent implements OnInit, OnDestroy, AfterViewInit, OnChanges {
    private subscriptions = new Subscription();

    @Select(TimesheetState.timesheets) timesheets$: Observable<Timesheet[]>;
    @Select(TimesheetState.selectedDate) selectedDate$: Observable<string>;
    @Input() showOnlyDoneTimesheets = false;

    filter;
    isLoading = true;
    sendingReminders = false;

    emojis: EMOJIS[] = [];

    timesheets: Timesheet[] = [];
    projects: Project[] = [];
    timeslots: Timeslot[] = [];
    employee: Employee;
    uniqueProjects: Project[] = [];
    allProjects: Project[] = [];
    employeesWithBudgets: EmployeeWithBudgets[] = [];
    private budgetedCostsByYear: EmployeeWithCosts[] = [];
    private currentSelectedDate: Date = new Date();
    mixedStatusProjectNames: string[] = [];
    displayedColumns: string[] = [
        'select',
        'projects',
        'invoiceReadiness',
        'department',
        'timesheetRequired',
        'isBilled',
        'timeslots',
        'totalHours',
        'expectedRevenue',
        'edit',
    ];
    dataSource: MatTableDataSource<Project>;
    @ViewChild(MatSort) sort: MatSort;
    @ViewChildren(MatSort) sorts: QueryList<MatSort>;
    dataSourcesByDept: { [key: string]: MatTableDataSource<Project> } = {};
    @ViewChild(TableExportDirective, { static: true }) exporter: TableExportDirective;
    private comanageSentByInvoiceNumber: Map<string, boolean> = new Map<string, boolean>();
    private comanageAmountByInvoiceRef: Map<string, number> = new Map<string, number>();
    selectedInvoiceProjectIds: Set<string> = new Set<string>();
    employeesWithoutBudget: { firstName: string; lastName: string }[] = [];
    private _allEmployees: Employee[] = [];

    constructor(
        public modalService: NgbModal,
        private timesheetService: TimesheetsService,
        private toastr: ToastrService,
        private minutesToMinutesAndHours: MinutesToMinutesAndHoursPipe,
        private logger: NGXLogger,
        private store: Store,
        private clipboard: Clipboard,
        private dialog: MatDialog,
        private employeeService: EmployeeService,
        private comanageService: ComanageService,
        private budgetService: BudgetService,
        private budgetParityService: BudgetParityService,
    ) {}

    openReportsModal(type: 'departments' | 'overtime') {
        const modalRef = this.modalService.open(ReportsModalComponent, {
            windowClass: 'reports-modal--wide',
            size: 'xl',
            animation: false,
        });
        modalRef.componentInstance.reportType = type;
        modalRef.componentInstance.timesheets = this.timesheets;
        modalRef.componentInstance.departments = this.departments;
    }

    ngOnInit(): void {
        this.emojis = Object.values(EMOJIS);
        // Load full projects list once to enrich data (timesheetType, etc.)
        this.subscriptions.add(
            this.employeeService.getAllProjects(true).subscribe((projects) => {
                this.allProjects = projects || [];
            }),
        );
        // Load budget data for hourly cost calculation
        this.subscriptions.add(
            this.budgetService.fetchEmployeesForBudget().subscribe((employees) => {
                this.employeesWithBudgets = employees || [];
                this.checkEmployeesWithoutBudget();
            }),
        );
        // Load all employees to detect missing budgets
        this.subscriptions.add(
            this.employeeService.getAllEmployees().subscribe((allEmps) => {
                this._allEmployees = allEmps || [];
                this.checkEmployeesWithoutBudget();
            }),
        );
        this.subscriptions.add(
            this.selectedDate$.subscribe((dateStr) => {
                if (dateStr) {
                    this.currentSelectedDate = new Date(dateStr);
                    // Load pre-computed budgeted costs for this year (same source as budgets overview)
                    const year = this.currentSelectedDate.getFullYear();
                    this.budgetService.getBudgetedCostsForYear(year).subscribe((costs) => {
                        this.budgetedCostsByYear = costs || [];
                    });
                    this.checkEmployeesWithoutBudget();
                }
                this.timesheets$.subscribe((t) => {
                    this.rebuildProjectData(t);

                    if (t) {
                        this.isLoading = false;
                    }
                });
            }),
        );

        this.loadComanageInvoiceStatuses();
    }

    private loadComanageInvoiceStatuses(): void {
        this.subscriptions.add(
            this.comanageService.getInvoices().subscribe(
                (response: any) => {
                    const invoices = this.parseInvoicesPayload(response);
                    const statusMap = new Map<string, boolean>();
                    const amountMap = new Map<string, number>();

                    invoices.forEach((invoice: any) => {
                        const sentState = this.isInvoiceSentState(invoice);
                        const references = this.getInvoiceReferences(invoice);
                        // Use totals.total (incl. BTW) — we compare against expected + 21% VAT
                        const total = invoice?.totals?.total ?? invoice?.total ?? null;
                        references.forEach((ref) => {
                            statusMap.set(ref, sentState);
                            if (total != null && !isNaN(Number(total))) {
                                amountMap.set(ref, Number(total));
                            }
                        });
                    });

                    this.comanageSentByInvoiceNumber = statusMap;
                    this.comanageAmountByInvoiceRef = amountMap;
                },
                (error) => {
                    this.logger.warn('Failed to load Comanage invoice statuses', error);
                    this.comanageSentByInvoiceNumber = new Map<string, boolean>();
                    this.comanageAmountByInvoiceRef = new Map<string, number>();
                },
            ),
        );
    }

    /**
     * Compare all active employees against employeesWithBudgets to find those
     * without any active (non-archived, date-overlapping) budget for the selected month.
     */
    private checkEmployeesWithoutBudget(): void {
        if (!this._allEmployees.length || !this.employeesWithBudgets) return;
        const refMs = this.currentSelectedDate.getTime();
        const budgetUuids = new Set<string>();

        (this.employeesWithBudgets || []).forEach((ewb) => {
            const hasActiveBudget = (ewb.budgets || []).some((b) => {
                if (b.archived) return false;
                const from = b.startDate ? new Date(b.startDate).getTime() : null;
                const to = b.endDate ? new Date(b.endDate).getTime() : null;
                const fromOk = !from || from <= refMs;
                const toOk = !to || to >= refMs;
                return fromOk && toOk;
            });
            if (hasActiveBudget) budgetUuids.add(ewb.uuid);
        });

        this.employeesWithoutBudget = this._allEmployees
            .filter((e) => !e.archived && e.uuid && !budgetUuids.has(e.uuid))
            .map((e) => ({ firstName: e.firstName || '', lastName: e.lastName || '' }))
            .sort((a, b) => a.firstName.localeCompare(b.firstName));
    }

    getEmployeesWithoutBudgetTooltip(): string {
        if (!this.employeesWithoutBudget.length) return '';
        const lines = this.employeesWithoutBudget.map((e) => `\u2022 ${e.firstName} ${e.lastName}`.trim());
        lines.unshift(`${this.employeesWithoutBudget.length} personeelslid${this.employeesWithoutBudget.length > 1 ? 'en' : ''} zonder budget:`);
        return lines.join('\n');
    }

    private parseInvoicesPayload(payload: any): any[] {
        if (Array.isArray(payload)) {
            return payload;
        }

        if (typeof payload === 'string') {
            try {
                const parsed = JSON.parse(payload);
                return Array.isArray(parsed) ? parsed : [];
            } catch {
                return [];
            }
        }

        if (payload?.data && Array.isArray(payload.data)) {
            return payload.data;
        }

        return [];
    }

    private normalizeInvoiceNumber(value: any): string {
        if (value === null || value === undefined) {
            return '';
        }
        return String(value).trim();
    }

    private canonicalInvoiceRef(value: string): string {
        return (value || '')
            .toUpperCase()
            .replace(/\s+/g, '')
            .replace(/[^A-Z0-9]/g, '');
    }

    private buildInvoiceRefCandidates(value: any): string[] {
        const normalized = this.normalizeInvoiceNumber(value);
        if (!normalized) {
            return [];
        }

        const canonical = this.canonicalInvoiceRef(normalized);
        const result = [normalized];
        if (canonical && canonical !== normalized) {
            result.push(canonical);
        }
        return result;
    }

    private getInvoiceReferences(invoice: any): string[] {
        const refs = new Set<string>();
        [invoice?.invoice_number, invoice?.number, invoice?.invoiceNumber, invoice?.id, invoice?.uuid, invoice?.invoice_id].forEach((value) =>
            this.buildInvoiceRefCandidates(value).forEach((candidate) => refs.add(candidate)),
        );
        return Array.from(refs);
    }

    private isInvoiceSentState(invoice: any): boolean {
        const status = String(invoice?.status ?? invoice?.state ?? '').toLowerCase();
        const sentAt = invoice?.sent_at ?? invoice?.sentAt ?? invoice?.date_sent ?? invoice?.dateSent;
        const sentFlag = invoice?.sent ?? invoice?.is_sent ?? invoice?.isSent;
        const paidAt = invoice?.paid_at ?? invoice?.paidAt ?? invoice?.date_paid ?? invoice?.datePaid;

        if (typeof sentFlag === 'boolean') {
            return sentFlag;
        }

        if (sentAt) {
            return true;
        }

        if (this.hasSentEvidence(invoice)) {
            return true;
        }

        if (status.includes('paid') || status.includes('betaald')) {
            return !!paidAt || !status.includes('unpaid');
        }

        return status.includes('sent') || status.includes('verzonden') || status.includes('email') || status.includes('mailed') || status.includes('delivered');
    }

    private hasSentEvidence(payload: any): boolean {
        if (!payload || typeof payload !== 'object') {
            return false;
        }

        const sentKeys = [
            'sent',
            'is_sent',
            'isSent',
            'sent_at',
            'sentAt',
            'date_sent',
            'dateSent',
            'email_sent',
            'emailSent',
            'email_sent_at',
            'emailSentAt',
            'last_sent_at',
            'lastSentAt',
            'mailed_at',
            'mailedAt',
        ];

        for (const key of sentKeys) {
            const value = payload[key];
            if (typeof value === 'boolean' && value) {
                return true;
            }
            if (typeof value === 'number' && value > 0) {
                return true;
            }
            if (typeof value === 'string' && value.trim().length > 0) {
                return true;
            }
        }

        const nestedKeys = ['delivery', 'deliveries', 'email', 'emails', 'meta', 'metadata', 'history'];
        for (const nestedKey of nestedKeys) {
            const nested = payload[nestedKey];
            if (Array.isArray(nested) && nested.some((entry) => this.hasSentEvidence(entry))) {
                return true;
            }
            if (!Array.isArray(nested) && this.hasSentEvidence(nested)) {
                return true;
            }
        }

        return false;
    }

    /** Split ';'-separated invoice-related field into individual values */
    private splitInvoiceField(value: string | null | undefined): string[] {
        if (!value) return [];
        return value
            .split(';')
            .map((v) => v.trim())
            .filter((v) => v.length > 0);
    }

    isComanageInvoiceSent(slot: Timeslot): boolean {
        if (!slot?.billed) {
            return false;
        }

        const slotStatus = String(slot?.comanageInvoiceStatus ?? '').toLowerCase();
        if (slotStatus.includes('sent') || slotStatus.includes('verzonden')) {
            return true;
        }

        // Support ';'-separated invoice numbers
        const numbers = this.splitInvoiceField(slot?.comanageInvoiceNumber);
        for (const num of numbers) {
            const candidates = this.buildInvoiceRefCandidates(num);
            if (candidates.some((candidate) => this.comanageSentByInvoiceNumber.get(candidate) === true)) {
                return true;
            }
        }

        return false;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.showOnlyDoneTimesheets && !changes.showOnlyDoneTimesheets.firstChange) {
            const latestTimesheets = this.store.selectSnapshot(TimesheetState.timesheets) || [];
            this.rebuildProjectData(latestTimesheets);
        }
    }

    getReadyToInvoiceStagesForProject(project: Project): ReadyToInvoiceStage[] {
        const stages: ReadyToInvoiceStage[] = [];
        const slots = this.getTimeslotsForProject(project) || [];
        const parentTimesheets = this.getParentTimesheetsForProject(project);

        const stage1Ok = parentTimesheets.length > 0 && parentTimesheets.every((timesheet) => timesheet?.status?.key === 'DONE');
        const timesheetsNotDone = parentTimesheets.filter((timesheet) => timesheet?.status?.key !== 'DONE');
        stages.push({
            key: '1',
            label: 'Status',
            passed: stage1Ok,
            reason: stage1Ok ? 'Alle betrokken timesheets staan op DONE.' : 'Niet alle betrokken timesheets staan op DONE.',
            details: stage1Ok
                ? [`${parentTimesheets.length} betrokken timesheet(s) zijn goedgekeurd.`]
                : this.limitDetails(
                      timesheetsNotDone.map((timesheet) => `${this.getTimesheetEmployeeName(timesheet)}: status ${timesheet?.status?.key || 'onbekend'}.`),
                  ),
        });

        const underHours = parentTimesheets.filter((timesheet) => {
            const required = Number(timesheet?.hoursRequired || 0);
            const worked = (this.getTotalMinutes(timesheet) || 0) / 60;
            return worked < required;
        });
        const stage2Ok = underHours.length === 0;
        stages.push({
            key: '2',
            label: 'Uren',
            passed: stage2Ok,
            reason: stage2Ok ? 'Alle betrokken timesheets halen de vereiste uren.' : `${underHours.length} betrokken timesheet(s) halen de vereiste uren niet.`,
            details: stage2Ok
                ? [`Alle ${parentTimesheets.length} betrokken timesheet(s) hebben genoeg uren.`]
                : this.limitDetails(
                      underHours.map((timesheet) => {
                          const required = Number(timesheet?.hoursRequired || 0);
                          const worked = (this.getTotalMinutes(timesheet) || 0) / 60;
                          return `${this.getTimesheetEmployeeName(timesheet)}: ${this.formatHours(worked)} / vereist ${this.formatHours(required)}.`;
                      }),
                  ),
        });

        const requiredSlots = slots.filter((slot: any) => slot?.project?.timesheetRequired === true);
        if (requiredSlots.length === 0) {
            stages.push({
                key: '3A',
                label: 'AI-validatie',
                passed: true,
                reason: 'Geen timesheet-validatie vereist voor dit project.',
                details: ['Dit project vraagt geen verplichte timesheet-bijlage.'],
            });
        } else {
            const missingAi = requiredSlots.filter((slot: any) => !this.hasAnyAiValidationData(slot));
            const stage3AOk = missingAi.length === 0;
            stages.push({
                key: '3A',
                label: 'AI-validatie',
                passed: stage3AOk,
                reason: stage3AOk
                    ? 'AI-validatie aanwezig voor alle verplichte timeslots.'
                    : `AI-validatie ontbreekt voor ${missingAi.length} verplichte timeslot(s).`,
                details: stage3AOk
                    ? [`AI-validatie gevonden op ${requiredSlots.length}/${requiredSlots.length} verplichte timeslot(s).`]
                    : this.limitDetails(missingAi.map((slot: any) => `${this.getSlotEmployeeName(slot)}: AI-validatie ontbreekt.`)),
            });
        }

        if (requiredSlots.length === 0) {
            stages.push({
                key: '3B',
                label: 'Bijlage en inhoud',
                passed: true,
                reason: 'Geen verplichte timesheet-bijlage voor dit project.',
                details: ['Er is geen verplichte PDF, urencontrole of handtekening nodig voor dit project.'],
            });
        } else {
            const invalidRequired = requiredSlots.filter((slot: any) => !this.isRequiredSlotValidationOk(slot));
            const stage3BOk = invalidRequired.length === 0;
            stages.push({
                key: '3B',
                label: 'Bijlage en inhoud',
                passed: stage3BOk,
                reason: stage3BOk
                    ? 'Verplichte bijlagen en validatie zijn OK voor dit project.'
                    : `Verplichte bijlage/validatie niet OK voor ${invalidRequired.length} timeslot(s).`,
                details: stage3BOk
                    ? [`PDF, gevalideerde uren en eventuele handtekening zijn OK voor ${requiredSlots.length} verplichte timeslot(s).`]
                    : this.limitDetails(this.collectDetails(invalidRequired, (slot: any) => this.getRequiredSlotValidationIssues(slot))),
            });
        }

        const billable = project?.billable === true;
        if (!billable) {
            stages.push({
                key: '4',
                label: 'Facturatiegegevens',
                passed: true,
                reason: 'Project is niet factureerbaar (billable = false).',
                details: ['Voor dit project zijn geen facturatiegegevens nodig.'],
            });
        } else {
            const hasContact = !!((this.allProjects || []).find((p) => p.uuid === project?.uuid)?.comanageContactId || project?.comanageContactId);
            const employeesWithoutRate = this.getProjectEmployeesWithoutRate(project, slots);
            const stage4Ok = hasContact && employeesWithoutRate.length === 0;
            const reasonParts: string[] = [];
            if (!hasContact) {
                reasonParts.push('Comanage contact ontbreekt op project.');
            }
            if (employeesWithoutRate.length > 0) {
                reasonParts.push(`Uurtarief ontbreekt voor ${employeesWithoutRate.length} medewerker(s).`);
            }
            stages.push({
                key: '4',
                label: 'Facturatiegegevens',
                passed: stage4Ok,
                reason: stage4Ok ? 'Comanage contact en uurtarieven zijn aanwezig.' : reasonParts.join(' '),
                details: stage4Ok
                    ? ['Comanage contact is ingevuld en alle betrokken medewerkers hebben een uurtarief.']
                    : this.limitDetails([
                          ...(!hasContact ? ['Comanage contact ontbreekt op project.'] : []),
                          ...employeesWithoutRate.map((employee) => `Uurtarief ontbreekt voor ${employee}.`),
                      ]),
            });
        }

        return stages;
    }

    getReadyStageTooltipLabel(stage: ReadyToInvoiceStage): string {
        const state = stage?.passed ? 'In orde' : 'Niet in orde';
        return `${stage?.key}. ${stage?.label}: ${state}. ${stage?.reason || ''}`.trim();
    }

    private getParentTimesheetsForProject(project: Project): Timesheet[] {
        const slots = this.getTimeslotsForProject(project) || [];
        const seen = new Set<string>();
        const result: Timesheet[] = [];
        slots.forEach((slot: any) => {
            const ts = slot?.timesheet;
            if (ts?.uuid && !seen.has(ts.uuid)) {
                seen.add(ts.uuid);
                result.push(ts);
            }
        });
        return result;
    }

    private hasAnyAiValidationData(slot: any): boolean {
        return !!(
            slot?.validatedConsultantName ||
            slot?.validatedTimesheetType ||
            (slot?.validatedHours !== null && slot?.validatedHours !== undefined) ||
            (slot?.validatedSigned !== null && slot?.validatedSigned !== undefined)
        );
    }

    private isRequiredSlotValidationOk(slot: any): boolean {
        if (!slot?.pdfUrl) {
            return false;
        }

        const expectedHours = this.getExpectedHoursForValidation(slot);
        const validatedHours = Number(slot?.validatedHours);
        const hasHours = slot?.validatedHours !== null && slot?.validatedHours !== undefined && !isNaN(validatedHours);
        const hoursOk = hasHours && Math.abs(validatedHours - expectedHours) < 0.51;

        const signRequired = slot?.project?.signedTimesheetRequired === true;
        const signOk = !signRequired || slot?.validatedSigned === true;

        return hoursOk && signOk;
    }

    private getExpectedHoursForValidation(slot: any): number {
        const workedHours = (Number(slot?.minutes || 0) + Number(slot?.leaveMinutes || 0)) / 60;
        if (slot?.project?.invoiceByDay) {
            const daysWorked = workedHours / 8;
            return Number((daysWorked * (slot?.project?.hoursPerDay ?? 8)).toFixed(2));
        }
        return Number(workedHours.toFixed(2));
    }

    private getRequiredSlotValidationIssues(slot: any): string[] {
        const issues: string[] = [];
        const employeeName = this.getSlotEmployeeName(slot);

        if (!slot?.pdfUrl) {
            issues.push(`${employeeName}: PDF-bijlage ontbreekt.`);
        }

        const expectedHours = this.getExpectedHoursForValidation(slot);
        const validatedHours = Number(slot?.validatedHours);
        const hasHours = slot?.validatedHours !== null && slot?.validatedHours !== undefined && !isNaN(validatedHours);
        if (!hasHours) {
            issues.push(`${employeeName}: gevalideerde uren ontbreken.`);
        } else if (Math.abs(validatedHours - expectedHours) >= 0.51) {
            issues.push(`${employeeName}: gevalideerde uren ${this.formatHours(validatedHours)} matchen niet met verwacht ${this.formatHours(expectedHours)}.`);
        }

        if (slot?.project?.signedTimesheetRequired === true && slot?.validatedSigned !== true) {
            issues.push(`${employeeName}: handtekening ontbreekt of is niet gevalideerd.`);
        }

        return issues;
    }

    private getProjectEmployeesWithoutRate(project: Project, slots: Timeslot[]): string[] {
        const fullProject = (this.allProjects || []).find((p) => p.uuid === project?.uuid) || project;
        const links = fullProject?.defaultLinkedEmployees || [];
        const missing = new Set<string>();

        const employeeMap = new Map<string, string>();
        (slots || []).forEach((slot: any) => {
            const uuid = slot?.timesheet?.employee?.uuid;
            if (!uuid) {
                return;
            }
            const name = [slot?.timesheet?.employee?.firstName, slot?.timesheet?.employee?.lastName].filter(Boolean).join(' ').trim() || uuid;
            employeeMap.set(uuid, name);
        });

        employeeMap.forEach((name, employeeUuid) => {
            const best = this.getBestRateLinkForEmployee(links as any[], employeeUuid, this.currentSelectedDate);
            const hasRate = Number(best?.pricePerHour || 0) > 0;
            if (!hasRate) {
                missing.add(name);
            }
        });

        return Array.from(missing);
    }

    private getTimesheetEmployeeName(timesheet?: Timesheet): string {
        return [timesheet?.employee?.firstName, timesheet?.employee?.lastName].filter(Boolean).join(' ').trim() || 'Onbekende medewerker';
    }

    private getSlotEmployeeName(slot: any): string {
        return this.getTimesheetEmployeeName(slot?.timesheet);
    }

    private formatHours(hours: number): string {
        return `${Math.max(0, Number(hours || 0)).toFixed(2)}u`;
    }

    private limitDetails(details: string[]): string[] {
        const uniqueDetails = Array.from(new Set(details.filter((detail) => !!detail)));
        const maxVisibleDetails = 5;
        if (uniqueDetails.length <= maxVisibleDetails) {
            return uniqueDetails;
        }
        return uniqueDetails.slice(0, maxVisibleDetails).concat(`Nog ${uniqueDetails.length - maxVisibleDetails} item(s)...`);
    }

    private collectDetails<T>(items: T[], getDetails: (item: T) => string[]): string[] {
        return (items || []).reduce((result: string[], item: T) => result.concat(getDetails(item)), []);
    }

    private rebuildProjectData(timesheetsInput: Timesheet[] = []) {
        const timesheets = JSON.parse(JSON.stringify(timesheetsInput || []));
        this.timesheets = timesheets;
        this.timeslots = [];
        this.uniqueProjects = [];

        const statusPerProject = new Map<string, { title: string; hasDone: boolean; hasOther: boolean }>();

        timesheets.forEach((timesheet) => {
            timesheet.totalHours = this.minutesToMinutesAndHours.transform(Timesheet.getTotalMinutes(timesheet));
            const isDoneTimesheet = timesheet?.status?.key === 'DONE';

            (timesheet.timeslots || []).forEach((slot: Timeslot) => {
                slot.timesheet = timesheet;

                const projectUuid = slot?.project?.uuid;
                if (projectUuid) {
                    const existingStatus = statusPerProject.get(projectUuid) || {
                        title: slot?.project?.title || 'Unnamed project',
                        hasDone: false,
                        hasOther: false,
                    };
                    if (isDoneTimesheet) {
                        existingStatus.hasDone = true;
                    } else {
                        existingStatus.hasOther = true;
                    }
                    statusPerProject.set(projectUuid, existingStatus);
                }

                if (!this.showOnlyDoneTimesheets || isDoneTimesheet) {
                    this.timeslots.push(slot);
                }
            });
        });

        this.timeslots.forEach((slot: Timeslot) => {
            const project = slot?.project;
            if (project?.uuid && !this.uniqueProjects.some((existingProject) => existingProject.uuid === project.uuid)) {
                this.uniqueProjects.push(project);
            }
        });

        this.uniqueProjects.sort((a, b) => a.title.localeCompare(b.title));

        const visibleProjectUuids = new Set((this.uniqueProjects || []).map((project) => project.uuid));
        this.mixedStatusProjectNames = Array.from(statusPerProject.entries())
            .filter(([projectUuid, status]) => status.hasDone && status.hasOther && visibleProjectUuids.has(projectUuid))
            .map(([, status]) => status.title)
            .sort((nameA, nameB) => nameA.localeCompare(nameB));

        this.dataSourcesByDept = {};
        for (const dept of this.departments) {
            const ds = new MatTableDataSource<Project>(this.projectsForDepartment(dept.key));
            this.setDataSourceOptions(ds);
            this.dataSourcesByDept[dept.key] = ds;
        }

        const sortsArray = this.sorts ? this.sorts.toArray() : [];
        for (let i = 0; i < this.departments.length; i++) {
            const dept = this.departments[i];
            const ds = this.dataSourcesByDept[dept.key];
            const s = sortsArray[i];
            if (ds && s) ds.sort = s;
        }

        this.applyFilter(this.filter || '');
    }

    ngAfterViewInit(): void {
        // Attach MatSort instances (one per department table) to corresponding data sources
        const sortsArray = this.sorts ? this.sorts.toArray() : [];
        for (let i = 0; i < this.departments.length; i++) {
            const dept = this.departments[i];
            const ds = this.dataSourcesByDept[dept.key];
            const s = sortsArray[i];
            if (ds && s) ds.sort = s;
        }
    }

    private setDataSourceOptions(ds: MatTableDataSource<Project>) {
        ds.filterPredicate = (data: Project, filter: string) => {
            return data.title.toLowerCase().includes(filter.toLowerCase());
        };
        ds.sortingDataAccessor = (item: any, property: string) => {
            if (property === 'department') return this.getDepartmentLabel(item).toLowerCase();
            if (property.includes('.')) return property.split('.').reduce((o, i) => o[i], item);
            const val = item[property];
            return typeof val === 'string' ? val.toLowerCase() : val;
        };
    }

    // Departments used for grouping / label lookup
    departments = [
        { key: 'CONSULTANCY', label: 'Consultancy (revenue)' },
        { key: 'WEBSITE', label: 'Websites (revenue)' },
        { key: 'CUSTOM', label: 'Custom (SOM) (revenue)' },
        { key: 'HOSTING', label: 'Hosting (revenue)' },
        { key: 'INTERN', label: 'Intern (cost)' },
        { key: 'OTHER', label: 'Other (revenue)' },
    ];

    isCostDepartment(deptKey: string): boolean {
        return deptKey === 'INTERN';
    }

    isProjectCostType(project: Project): boolean {
        return this.isCostDepartment(String(project?.projectType || 'OTHER'));
    }

    projectsForDepartment(deptKey: string): Project[] {
        return (this.uniqueProjects || []).filter((p) => (p.projectType || 'OTHER') === deptKey);
    }

    // Compute total and billed minutes for a given department key
    public getDepartmentTotals(deptKey: string): { totalMinutes: number; billedMinutes: number } {
        const projects = this.projectsForDepartment(deptKey) || [];
        let total = 0;
        let billed = 0;
        projects.forEach((project) => {
            const slots = this.getTimeslotsForProject(project) || [];
            slots.forEach((s) => {
                total += Number(s.minutes || 0);
                if (s.billed) billed += Number(s.minutes || 0);
            });
        });
        return { totalMinutes: total, billedMinutes: billed };
    }

    // Format minutes -> hours string with 'u' suffix, integer if whole hours else one decimal
    public formatMinutesAsHoursLabel(minutes: number): string {
        const hours = minutes / 60;
        if (!isFinite(hours) || isNaN(hours)) return '0,0u';
        // Always round to one decimal place (one digit after the comma)
        // Use comma as decimal separator for locale-style display (matching 'u' suffix)
        const formatted = hours.toFixed(1).replace('.', ',');
        return `${formatted}u`;
    }

    // Build a short summary string for a department: "(99% billed: 100u/101u)"
    public departmentSummaryLabel(deptKey: string): string {
        const t = this.getDepartmentTotals(deptKey);
        const totalHoursLabel = this.formatMinutesAsHoursLabel(t.totalMinutes);
        const billedHoursLabel = this.formatMinutesAsHoursLabel(t.billedMinutes);
        const percentRaw = t.totalMinutes > 0 ? (t.billedMinutes / t.totalMinutes) * 100 : 100;
        const percentFormatted = !isFinite(percentRaw) || isNaN(percentRaw) ? '100,0' : percentRaw.toFixed(1).replace('.', ',');
        return `(${percentFormatted}% billed: ${billedHoursLabel}/${totalHoursLabel})`;
    }

    dataSourceForDepartment(deptKey: string) {
        return new MatTableDataSource<Project>(this.projectsForDepartment(deptKey));
    }

    getDepartmentLabel(project: Project): string {
        const key = (project?.projectType || 'OTHER').toString();
        const found = this.departments.find((d) => d.key === key);
        if (found) return found.label;
        return project?.projectType ? String(project.projectType) : 'Other';
    }

    getTimesheetTypeFor(project: Project): string {
        const p = (this.allProjects || []).find((x) => x.uuid === project?.uuid);
        return (p?.timesheetType || project?.timesheetType || '').toString();
    }

    getTimeslotsForProject(project: Project): Timeslot[] {
        const slots: Timeslot[] = [];
        this.timeslots.forEach((slot) => {
            if (slot.project.uuid === project.uuid) {
                slots.push(slot);
            }
        });
        return slots;
    }

    isProjectSelectedForInvoice(project: Project): boolean {
        return !!project?.uuid && this.selectedInvoiceProjectIds.has(project.uuid);
    }

    toggleProjectForInvoice(project: Project, checked: boolean): void {
        if (!project?.uuid) return;
        if (checked) {
            this.selectedInvoiceProjectIds.add(project.uuid);
        } else {
            this.selectedInvoiceProjectIds.delete(project.uuid);
        }
    }

    getSelectedInvoiceProjects(): Project[] {
        return (this.uniqueProjects || []).filter((project) => !!project?.uuid && this.selectedInvoiceProjectIds.has(project.uuid));
    }

    openSelectedProjectsInvoiceDialog(): void {
        const selectedProjects = this.getSelectedInvoiceProjects();
        const timeslots = selectedProjects.reduce((acc: Timeslot[], project) => {
            acc.push(...this.getTimeslotsForProject(project));
            return acc;
        }, []);
        if (timeslots.length === 0) {
            this.toastr.warning('Selecteer minstens één project met timeslots.');
            return;
        }
        this.openInvoiceDialog(timeslots);
    }

    getTotalWorkedMinutesForProject(project: Project): number {
        const slots = [];
        let minutes = 0;
        this.timeslots.forEach((slot) => {
            if (slot.project.uuid === project.uuid) {
                slots.push(slot);
            }
        });
        slots.forEach((s) => {
            minutes += s.minutes;
        });
        return minutes;
    }

    getEmployeeByTimeslot(timeslot: Timeslot) {
        this.employee = null;
        this.timesheets.forEach((sheet) => {
            sheet.timeslots.forEach((slot) => {
                if (slot.uuid === timeslot.uuid) {
                    this.employee = sheet.employee;
                }
            });
        });

        return this.employee;
    }

    applyFilter(filterValue) {
        const f = filterValue ? filterValue.trim().toLowerCase() : '';
        Object.keys(this.dataSourcesByDept || {}).forEach((k) => {
            const ds = this.dataSourcesByDept[k];
            if (ds) ds.filter = f;
        });
    }

    addNewTimesheet() {
        const employees = [];
        this.timesheets.forEach((el) => employees.push(el.employee));
        this.logger.debug('Opening addTimesheetModal');
        const modalRef = this.modalService.open(AddTimesheetModalComponent, { windowClass: 'modal-prompt' });
        modalRef.componentInstance.currentEmployees = employees;
    }

    sendReminders() {
        this.sendingReminders = true;
        this.subscriptions.add(
            // take(1): this reads the month that is on screen now. Without it the subscription
            // stays alive for the life of the component and fires again on every month change, so
            // one press of Send reminders mailed every employee again each time the user stepped
            // to another month -- and a second press added a second subscription on top.
            this.selectedDate$.pipe(take(1)).subscribe((selectedDate) => {
                this.logger.debug('Attempt to send reminders');
                this.timesheetService.sendReminders(selectedDate).subscribe(
                    (result) => {
                        this.sendingReminders = false;
                        this.logger.debug('Succesfully sent reminders');
                        this.toastr.success('Reminders where sent successfully.');
                    },
                    (err) => {
                        this.sendingReminders = false;
                        this.logger.error('Failed to send reminders');
                        this.toastr.error('Oops, something went wrong. Please try again later.');
                    },
                );
            }),
        );
    }

    openCronjobSettings() {
        this.logger.debug('Opening cronjobRulesSettingsModal');
        const modalRef = this.modalService.open(CronjobRulesSettingsModalComponent, {
            windowClass: 'modal-pane',
            animation: false,
        });
    }

    deleteTimesheet(timesheet: Timesheet) {
        this.logger.debug('starting remove timesheet flow');
        const modalRef = this.modalService.open(ConfirmationModalComponent, {
            windowClass: 'modal-prompt',
            animation: false,
        });
        modalRef.componentInstance.type = 'Delete';
        modalRef.componentInstance.title = 'Delete timesheet';
        modalRef.componentInstance.message =
            'Are you sure you want to delete ' + timesheet.employee.firstName + "'s timesheet? \n All linked timeslots will be removed allong with it.";

        this.subscriptions.add(
            modalRef.componentInstance.closeEvent.subscribe((val) => {
                if (val) {
                    this.logger.debug('attempting to remove timesheet');
                    this.store.dispatch(new RemoveTimesheet(timesheet.uuid)).subscribe(
                        () => {
                            this.logger.debug('successfully removed timesheet');
                            this.toastr.info('Timesheet was removed successfully.');
                        },
                        (error) => {
                            this.logger.error(error);
                            this.toastr.error('Oops, something went wrong please try again later.');
                        },
                    );
                }
            }),
        );
    }

    changeStatus(status: string, timesheet: Timesheet) {
        this.subscriptions.add(
            this.store.dispatch(new ChangeTimesheetStatus(timesheet.uuid, TIMESHEET_STATUS.getStatus(status))).subscribe(
                () => {
                    this.logger.debug('Succesfully updated timesheet');
                },
                (error) => {
                    this.logger.error('Failed to update timesheet');
                },
            ),
        );
    }

    copyUrlToClipboard(timesheet: Timesheet) {
        this.clipboard.copy(timesheet.url);
    }

    sendReminder(timesheet: Timesheet) {
        this.subscriptions.add(this.timesheetService.sendReminder(timesheet.uuid).subscribe());
    }

    getTotalMinutes = Timesheet.getTotalMinutes;
    getTotalDaysFromTimesheet = Timesheet.getTotalDaysFromTimesheet;
    getTotalDaysFromMinutes = Timesheet.getTotalDaysFromMinutes;
    getRequiredHoursDiff = Timesheet.getRequiredHoursDiff;
    getRequiredHoursDiffPercentage = Timesheet.getRequiredHoursDiffPercentage;

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
        this.uniqueProjects = [];
    }

    openTimesheetDetailModal(timesheet: Timesheet) {
        const modalRef = this.modalService.open(TimesheetInfoModalComponent, {
            windowClass: 'modal-pane',
            animation: false,
        });
        modalRef.componentInstance.timesheet = timesheet;
    }

    openInvoiceDialog(timeslots: Timeslot[]): void {
        const dialogRef = this.dialog.open(InvoiceDialogComponent, {
            width: '800px',
            data: {
                projectTimeslots: timeslots,
                timesheets: this.timesheets,
                uniqueProjects: this.uniqueProjects,
            },
        });

        dialogRef.afterClosed().subscribe((result: any) => {
            // Only process when dialog returns the structured object (successful invoice creation).
            // Error-path closes pass just the array and should NOT mark timeslots as billed.
            if (result && !Array.isArray(result) && result.timeslots && result.timeslots.length > 0) {
                const pdfUrl: string | undefined = result.pdfUrl || undefined;
                const invoiceNumber: string | undefined = result.invoiceNumber ? String(result.invoiceNumber) : undefined;
                const invoiceId: string | undefined = result.invoiceId ? String(result.invoiceId) : undefined;
                const invoiceStatus: string | undefined = result.invoiceStatus ? String(result.invoiceStatus) : undefined;
                const invoiceDate: string | undefined = result.invoiceDate ? String(result.invoiceDate) : undefined;
                const comanageLink: string | undefined = result.comanageLink || undefined;

                result.timeslots.forEach((slot: Timeslot) => {
                    // Resolve timesheet UUID: prefer the back-reference, fall back to a lookup
                    let timesheetUUID = slot.timesheet?.uuid;
                    if (!timesheetUUID) {
                        const parent = (this.timesheets || []).find((ts) => (ts.timeslots || []).some((s) => s.uuid === slot.uuid));
                        timesheetUUID = parent?.uuid;
                    }
                    // Pass invoice reference to backend so it persists on the timeslot
                    this.timesheetService
                        .setTimeslotToIsBilled(slot.uuid, invoiceNumber, invoiceId, invoiceStatus, invoiceDate, pdfUrl, comanageLink)
                        .subscribe();
                    this.store.dispatch(
                        new SetTimeslotToIsBilled(timesheetUUID, slot.uuid, true, invoiceNumber, invoiceId, invoiceStatus, invoiceDate, pdfUrl, comanageLink),
                    );
                });
            }
        });
    }

    openLinkInvoiceDialog(timeslots: Timeslot[]): void {
        const dialogRef = this.dialog.open(LinkInvoiceDialogComponent, {
            width: '520px',
            data: {
                projectTimeslots: timeslots,
            },
        });

        dialogRef.afterClosed().subscribe((result: any) => {
            if (!result || !result.timeslots || result.timeslots.length === 0) {
                return;
            }

            const invoiceNumber: string | undefined = result.invoiceNumber ? String(result.invoiceNumber) : undefined;
            const invoiceId: string | undefined = result.invoiceId ? String(result.invoiceId) : undefined;
            const invoiceStatus: string | undefined = result.invoiceStatus ? String(result.invoiceStatus) : undefined;
            const invoiceDate: string | undefined = result.invoiceDate ? String(result.invoiceDate) : undefined;
            const pdfUrl: string | undefined = result.pdfUrl || undefined;
            const comanageLink: string | undefined = result.comanageLink || undefined;

            // Support multiple invoices: find slots that can accept the new invoice
            const slotsWithoutInvoice: Timeslot[] = [];
            const alreadyBilledWithOther: Timeslot[] = [];
            const alreadyHasThisInvoice: Timeslot[] = [];

            (result.timeslots || []).forEach((slot: Timeslot) => {
                if (!slot.billed || !slot.comanageInvoiceNumber) {
                    slotsWithoutInvoice.push(slot);
                } else {
                    // Check if this slot already contains the new invoice number
                    const existingNumbers = this.splitInvoiceField(slot.comanageInvoiceNumber);
                    if (invoiceNumber && existingNumbers.includes(invoiceNumber)) {
                        alreadyHasThisInvoice.push(slot);
                    } else {
                        alreadyBilledWithOther.push(slot);
                    }
                }
            });

            if (alreadyHasThisInvoice.length > 0 && slotsWithoutInvoice.length === 0 && alreadyBilledWithOther.length === 0) {
                this.toastr.warning('Deze factuur is al gelinkt aan dit project.');
                return;
            }

            // Determine which slots get the new invoice
            if (slotsWithoutInvoice.length > 0) {
                // Assign to unbilled slots directly
                slotsWithoutInvoice.forEach((slot: Timeslot) => {
                    this.updateTimeslotInvoice(slot, invoiceNumber, invoiceId, invoiceStatus, invoiceDate, pdfUrl, comanageLink);
                });
                this.toastr.success('Bestaande factuur gelinkt aan geselecteerde timeslots.');
            } else if (alreadyBilledWithOther.length > 1) {
                // Multiple timeslots with other invoice — reassign ONE to the new invoice
                const targetSlot = alreadyBilledWithOther.pop()!;
                this.updateTimeslotInvoice(targetSlot, invoiceNumber, invoiceId, invoiceStatus, invoiceDate, pdfUrl, comanageLink);
                this.toastr.success(`Factuur gelinkt. ${alreadyBilledWithOther.length} timeslots behouden eerdere factuur.`);
            } else if (alreadyBilledWithOther.length === 1) {
                // Only 1 timeslot with a different invoice — APPEND (combine with ';')
                const targetSlot = alreadyBilledWithOther[0];
                const combinedNumber = [targetSlot.comanageInvoiceNumber, invoiceNumber].filter(Boolean).join(';');
                const combinedPdfUrl = [targetSlot.comanageInvoicePdfUrl, pdfUrl].filter(Boolean).join(';');
                const combinedLink = [targetSlot.comanageInvoiceLink, comanageLink].filter(Boolean).join(';');
                this.updateTimeslotInvoice(targetSlot, combinedNumber, invoiceId, invoiceStatus, invoiceDate, combinedPdfUrl, combinedLink);
                this.toastr.success('Tweede factuur toegevoegd aan dit project.');
            }
        });
    }

    /** Helper: update a single timeslot with invoice info (API + store) */
    private updateTimeslotInvoice(
        slot: Timeslot,
        invoiceNumber: string | undefined,
        invoiceId: string | undefined,
        invoiceStatus: string | undefined,
        invoiceDate: string | undefined,
        pdfUrl: string | undefined,
        comanageLink: string | undefined,
    ): void {
        let timesheetUUID = slot.timesheet?.uuid;
        if (!timesheetUUID) {
            const parent = (this.timesheets || []).find((ts) => (ts.timeslots || []).some((s) => s.uuid === slot.uuid));
            timesheetUUID = parent?.uuid;
        }

        this.timesheetService.setTimeslotToIsBilled(slot.uuid, invoiceNumber, invoiceId, invoiceStatus, invoiceDate, pdfUrl, comanageLink).subscribe();

        this.store.dispatch(
            new SetTimeslotToIsBilled(timesheetUUID, slot.uuid, true, invoiceNumber, invoiceId, invoiceStatus, invoiceDate, pdfUrl, comanageLink),
        );
    }

    /** Unlink all invoices from a project's timeslots */
    unlinkInvoice(project: Project): void {
        const slots = this.getTimeslotsForProject(project) || [];
        const billedSlots = slots.filter((s) => s.billed);
        if (billedSlots.length === 0) {
            this.toastr.warning('Er zijn geen gefactureerde timeslots bij dit project.');
            return;
        }

        const invoiceNumbers = this.getInvoiceNumbers(project);
        const label = invoiceNumbers.length > 0 ? `factuur ${invoiceNumbers.join(', ')}` : 'facturatiegegevens';

        if (!confirm(`${label} ontkoppelen van "${project.title}"?\n\nDit reset de billed-status van ${billedSlots.length} timeslot(s).`)) {
            return;
        }

        billedSlots.forEach((slot) => {
            let timesheetUUID = slot.timesheet?.uuid;
            if (!timesheetUUID) {
                const parent = (this.timesheets || []).find((ts) => (ts.timeslots || []).some((s) => s.uuid === slot.uuid));
                timesheetUUID = parent?.uuid;
            }

            this.timesheetService.unbillTimeslot(slot.uuid).subscribe();
            this.store.dispatch(new UnbillTimeslot(timesheetUUID, slot.uuid));
        });

        this.toastr.success(`${label} ontkoppeld van "${project.title}".`);
    }

    hasNoInvoice(project: any): boolean {
        return (this.getTimeslotsForProject(project) || []).some((s) => !!s.noInvoiceReason);
    }

    getNoInvoiceReason(project: any): string {
        const slot = (this.getTimeslotsForProject(project) || []).find((s) => !!s.noInvoiceReason);
        return slot?.noInvoiceReason || '';
    }

    getNoInvoiceAttachmentUrl(project: any): string {
        const slot = (this.getTimeslotsForProject(project) || []).find((s) => !!s.noInvoiceAttachmentUrl);
        return slot?.noInvoiceAttachmentUrl || '';
    }

    openNoInvoiceDialog(project: any): void {
        const timeslots = this.getTimeslotsForProject(project) || [];
        if (timeslots.length === 0) {
            this.toastr.warning('Geen timeslots beschikbaar voor dit project.');
            return;
        }

        const dialogRef = this.dialog.open(NoInvoiceDialogComponent, {
            width: '480px',
            data: {
                projectTimeslots: timeslots,
                existingReason: this.getNoInvoiceReason(project),
                existingAttachmentUrl: this.getNoInvoiceAttachmentUrl(project),
            },
        });

        dialogRef.afterClosed().subscribe((result: any) => {
            if (!result) return;

            timeslots.forEach((slot: Timeslot) => {
                if (!slot.uuid) return;
                const timesheetUUID = this.resolveTimesheetUUID(slot);
                this.timesheetService.setNoInvoice(slot.uuid, result.reason, result.attachment).subscribe({
                    next: () => {
                        this.store.dispatch(new SetNoInvoice(timesheetUUID, slot.uuid, result.reason));
                        this.toastr.success('"Geen factuur" markering opgeslagen.');
                    },
                    error: () => this.toastr.error('Opslaan mislukt. Probeer opnieuw.'),
                });
            });
        });
    }

    clearNoInvoice(project: any): void {
        const timeslots = (this.getTimeslotsForProject(project) || []).filter((s) => !!s.noInvoiceReason);
        timeslots.forEach((slot: Timeslot) => {
            if (!slot.uuid) return;
            const timesheetUUID = this.resolveTimesheetUUID(slot);
            this.timesheetService.clearNoInvoice(slot.uuid).subscribe();
            this.store.dispatch(new ClearNoInvoice(timesheetUUID, slot.uuid));
        });
        this.toastr.success('"Geen factuur" markering verwijderd.');
    }

    private resolveTimesheetUUID(slot: Timeslot): string {
        if (slot.timesheet?.uuid) return slot.timesheet.uuid;
        const parent = (this.timesheets || []).find((ts) => (ts.timeslots || []).some((s) => s.uuid === slot.uuid));
        return parent?.uuid;
    }

    /** Get ALL unique Comanage invoice numbers for a project (supports ';'-separated) */
    getInvoiceNumbers(project: any): string[] {
        const slots = this.getTimeslotsForProject(project) || [];
        const seen = new Set<string>();
        for (const slot of slots) {
            if (slot.comanageInvoiceNumber) {
                this.splitInvoiceField(slot.comanageInvoiceNumber).forEach((n) => seen.add(n));
            }
            (slot.invoiceAllocations || []).forEach((allocation) => {
                if (allocation?.invoiceNumber) {
                    seen.add(String(allocation.invoiceNumber));
                }
            });
        }
        return Array.from(seen);
    }

    /** Get structured invoice items for a project (one per unique invoice, supports ';'-separated) */
    getInvoiceItems(project: any): { number: string; pdfUrl: string | null; link: string | null }[] {
        const slots = this.getTimeslotsForProject(project) || [];
        const seen = new Set<string>();
        const items: { number: string; pdfUrl: string | null; link: string | null }[] = [];
        for (const slot of slots) {
            if (slot.comanageInvoiceNumber) {
                const numbers = this.splitInvoiceField(slot.comanageInvoiceNumber);
                const pdfUrls = this.splitInvoiceField(slot.comanageInvoicePdfUrl);
                const links = this.splitInvoiceField(slot.comanageInvoiceLink);
                numbers.forEach((num, idx) => {
                    if (!seen.has(num)) {
                        seen.add(num);
                        items.push({
                            number: num,
                            pdfUrl: pdfUrls[idx] || null,
                            link: links[idx] || null,
                        });
                    }
                });
            }
            (slot.invoiceAllocations || []).forEach((allocation) => {
                const num = allocation?.invoiceNumber ? String(allocation.invoiceNumber) : '';
                if (!num || seen.has(num)) {
                    return;
                }
                seen.add(num);
                items.push({
                    number: num,
                    pdfUrl: allocation.invoicePdfUrl || null,
                    link: allocation.invoiceLink || null,
                });
            });
        }
        return items;
    }

    /** Get the first Comanage invoice PDF URL for a project (from any of its billed timeslots) */
    getInvoicePdfUrl(project: any): string | null {
        const slots = this.getTimeslotsForProject(project) || [];
        for (const slot of slots) {
            if (slot.comanageInvoicePdfUrl) return slot.comanageInvoicePdfUrl;
        }
        return null;
    }

    /** Get the first Comanage invoice number for a project (from any of its billed timeslots) */
    getInvoiceNumber(project: any): string | null {
        const slots = this.getTimeslotsForProject(project) || [];
        for (const slot of slots) {
            if (slot.comanageInvoiceNumber) return slot.comanageInvoiceNumber;
        }
        return null;
    }

    /** Get the first Comanage invoice link for a project (from any of its billed timeslots) */
    getInvoiceLink(project: any): string | null {
        const slots = this.getTimeslotsForProject(project) || [];
        for (const slot of slots) {
            if (slot.comanageInvoiceLink) return slot.comanageInvoiceLink;
        }
        return null;
    }

    hasBilledTimeslots(project: any): boolean {
        const slots = this.getTimeslotsForProject(project) || [];
        return slots.some((slot) => !!slot?.billed || (slot?.invoiceAllocations || []).length > 0);
    }

    /** Look up the invoiced amount from Comanage for a project, summing ALL linked invoices */
    getInvoicedAmountForProject(project: Project): number | null {
        const invoiceNumbers = this.getInvoiceNumbers(project);
        if (!invoiceNumbers.length) return null;
        let total = 0;
        let found = false;
        for (const num of invoiceNumbers) {
            const candidates = this.buildInvoiceRefCandidates(num);
            for (const candidate of candidates) {
                if (this.comanageAmountByInvoiceRef.has(candidate)) {
                    total += this.comanageAmountByInvoiceRef.get(candidate);
                    found = true;
                    break; // found amount for this invoice number, move to next
                }
            }
        }
        return found ? total : null;
    }

    /**
     * Compare invoiced amount vs expected revenue.
     * Returns 'match' | 'over' | 'under' | null (no invoice data)
     */
    getRevenueComparison(project: Project): 'match' | 'over' | 'under' | null {
        const invoiced = this.getInvoicedAmountForProject(project);
        if (invoiced == null) return null;
        const expected = Math.abs(this.getExpectedRevenueForProject(project));
        const expectedInclBtw = expected * 1.21;
        const diff = invoiced - expectedInclBtw;
        // Allow €1 tolerance for rounding
        if (Math.abs(diff) <= 1) return 'match';
        return diff > 0 ? 'over' : 'under';
    }

    getRevenueComparisonTooltip(project: Project): string {
        const invoiced = this.getInvoicedAmountForProject(project);
        if (invoiced == null) return '';
        const expected = Math.abs(this.getExpectedRevenueForProject(project));
        const expectedInclBtw = expected * 1.21;
        const diff = invoiced - expectedInclBtw;
        const invoiceNumbers = this.getInvoiceNumbers(project);
        const invoiceLabel = invoiceNumbers.length > 1 ? `Gefactureerd (${invoiceNumbers.length} facturen, incl. BTW)` : 'Gefactureerd (incl. BTW)';
        return `${invoiceLabel}: €${invoiced.toFixed(2)} | Verwacht (incl. 21% BTW): €${expectedInclBtw.toFixed(2)} | Verschil: €${diff.toFixed(2)}`;
    }

    getExpectedRevenueForProject(project: Project): number {
        const total = this.getRevenueBreakdown(project).total;
        return this.isProjectCostType(project) ? -total : total;
    }

    getRevenueTooltip(project: Project): string {
        const b = this.getRevenueBreakdown(project);
        if (b.error) return b.error;
        if (!b.lines.length) return 'Geen timeslots gevonden';
        const isCost = this.isProjectCostType(project);
        const parts: string[] = [];

        // Show project billing settings at the top
        if (!isCost) {
            const fullProject = (this.allProjects || []).find((p) => p.uuid === project?.uuid);
            if (fullProject) {
                const invoiceByDay = !!fullProject.invoiceByDay;
                const hoursPerDay = fullProject.hoursPerDay || 8;
                parts.push(`Facturatie: ${invoiceByDay ? 'per dag' : 'per uur'}${invoiceByDay ? ` (${hoursPerDay}u/dag)` : ''}`);
                parts.push('─'.repeat(40));
            }
        }

        // Per-person lines with cost + margin
        b.lines.forEach((l) => {
            parts.push(l.text);
            if (!isCost) {
                parts.push(`  → Kost: €${l.cost.toFixed(2)} | Marge: €${l.margin.toFixed(2)}`);
            }
        });

        parts.push('─'.repeat(40));
        if (isCost) {
            const displayTotal = -b.total;
            parts.push(`Totaal: €${displayTotal.toFixed(2)} (cost)`);
        } else {
            const totalMargin = b.total - b.totalCost;
            parts.push(`Totaal omzet: €${b.total.toFixed(2)}`);
            parts.push(`Totaal kost:  €${b.totalCost.toFixed(2)}`);
            parts.push(`Totaal marge: €${totalMargin.toFixed(2)}`);
        }
        return parts.join('\n');
    }

    getEmployeeCostForSlot(slot: Timeslot, project: Project): string | null {
        if (!this.isProjectCostType(project)) return null;
        const employee = slot?.timesheet?.employee;
        if (!employee?.uuid) return null;
        const rate = this.getBudgetHourlyRate(employee.uuid);
        const hours = (slot.minutes || 0) / 60;
        const cost = -(hours * rate);
        return cost.toFixed(2);
    }

    getEmployeeCostTooltip(slot: Timeslot, project: Project): string {
        if (!this.isProjectCostType(project)) return '';
        const employee = slot?.timesheet?.employee;
        if (!employee?.uuid) return '';
        const rate = this.getBudgetHourlyRate(employee.uuid);
        const hours = (slot.minutes || 0) / 60;
        const cost = hours * rate;
        return `${hours.toFixed(1)}u × €${rate.toFixed(2)}/u = €${cost.toFixed(2)}`;
    }

    /**
     * Get the hourly budget rate for an employee, matching the Budgets overview exactly.
     * Uses backend pre-computed costs as primary source (same as budgets-list), falls back to parity service.
     */
    getBudgetHourlyRate(employeeUuid: string): number {
        const year = this.currentSelectedDate.getFullYear();
        const month = this.currentSelectedDate.getMonth();
        const fromState = this.budgetParityService.getHourlyCostFromBudgetedState(
            employeeUuid,
            year,
            month,
            this.budgetedCostsByYear,
            this.employeesWithBudgets,
        );
        if (fromState != null) return fromState;
        // Fallback: compute from raw budget data
        return this.budgetParityService.getHourlyBudgetedCostForEmployee(employeeUuid, this.currentSelectedDate, this.employeesWithBudgets);
    }

    /**
     * Find the best rate link for an employee on a project, considering effectiveFrom/effectiveTo dates.
     * Matches the logic from invoice-dialog's getBestPriceLinkByDate but improved for "all expired" case.
     */
    private getBestRateLinkForEmployee(links: any[], employeeUuid: string, refDate: Date): any | null {
        const employeeLinks = (links || []).filter((l) => l.employeeId === employeeUuid);
        if (!employeeLinks.length) return null;
        if (employeeLinks.length === 1) return employeeLinks[0];

        const refMs = refDate.getTime();
        const parsed = employeeLinks.map((l) => ({
            raw: l,
            from: l.effectiveFrom ? new Date(l.effectiveFrom).getTime() : null,
            to: l.effectiveTo ? new Date(l.effectiveTo).getTime() : null,
        }));

        // 1. Find links active during the reference date
        const eligible = parsed.filter((l) => {
            const fromOk = !l.from || l.from <= refMs;
            const toOk = !l.to || l.to >= refMs;
            return fromOk && toOk;
        });
        if (eligible.length) {
            // Pick the one with the most recent effectiveFrom
            eligible.sort((a, b) => (b.from || -Infinity) - (a.from || -Infinity));
            return eligible[0].raw;
        }

        // 2. No active link — pick the most recently expired (latest effectiveTo)
        const expired = parsed.filter((l) => l.to && l.to < refMs);
        if (expired.length) {
            expired.sort((a, b) => b.to! - a.to!);
            return expired[0].raw;
        }

        // 3. All future — pick the earliest future link
        const future = parsed.filter((l) => l.from && l.from > refMs);
        if (future.length) {
            future.sort((a, b) => a.from! - b.from!);
            return future[0].raw;
        }

        return parsed[0].raw;
    }

    private getRevenueBreakdown(project: Project): {
        total: number;
        totalCost: number;
        lines: { text: string; rev: number; cost: number; margin: number; name: string }[];
        error?: string;
    } {
        const isCost = this.isProjectCostType(project);

        // For INTERN (cost) projects, use budget hourly rates
        if (isCost) {
            let total = 0;
            const lines: { text: string; rev: number; cost: number; margin: number; name: string }[] = [];
            const slots = this.getTimeslotsForProject(project) || [];

            const byEmployee = new Map<string, { name: string; minutes: number; employeeUuid: string }>();
            slots.forEach((slot) => {
                const employee = slot?.timesheet?.employee;
                const employeeUuid = employee?.uuid;
                if (!employeeUuid) return;
                const name = [employee.firstName, employee.lastName].filter(Boolean).join(' ') || employeeUuid;
                const existing = byEmployee.get(employeeUuid);
                if (existing) {
                    existing.minutes += slot.minutes || 0;
                } else {
                    byEmployee.set(employeeUuid, { name, minutes: slot.minutes || 0, employeeUuid });
                }
            });

            byEmployee.forEach((entry) => {
                const hours = entry.minutes / 60;
                const rate = this.getBudgetHourlyRate(entry.employeeUuid);
                if (rate > 0) {
                    const cost = hours * rate;
                    total += cost;
                    lines.push({
                        text: `${entry.name}: ${hours.toFixed(1)}u × €${rate.toFixed(2)}/u = €${cost.toFixed(2)}`,
                        rev: 0,
                        cost,
                        margin: -cost,
                        name: entry.name,
                    });
                } else {
                    lines.push({ text: `${entry.name}: ${hours.toFixed(1)}u × ⚠ geen budget tarief → €0`, rev: 0, cost: 0, margin: 0, name: entry.name });
                }
            });

            return { total, totalCost: total, lines };
        }

        // For revenue projects, use project-linked pricePerHour
        const fullProject = (this.allProjects || []).find((p) => p.uuid === project?.uuid);
        const links = fullProject?.defaultLinkedEmployees;
        if (!fullProject) {
            return { total: 0, lines: [], totalCost: 0, error: 'Project niet gevonden in projectlijst (mogelijk gearchiveerd?)' };
        }
        if (!links?.length) {
            return { total: 0, lines: [], totalCost: 0, error: 'Geen tarieven geconfigureerd voor dit project' };
        }

        const invoiceByDay = !!fullProject.invoiceByDay;
        const hoursPerDay = fullProject.hoursPerDay || 8;
        const rateConversionHpd = 8; // pricePerHour is always stored as dayRate/8 (matches invoice-dialog)

        let total = 0;
        let totalCost = 0;
        const lines: { text: string; rev: number; cost: number; margin: number; name: string }[] = [];
        const slots = this.getTimeslotsForProject(project) || [];

        // Group by employee for cleaner output
        const byEmployee = new Map<string, { name: string; minutes: number; rate: number | null; employeeUuid: string }>();
        slots.forEach((slot) => {
            const employee = slot?.timesheet?.employee;
            const employeeUuid = employee?.uuid;
            if (!employeeUuid) return;
            const name = [employee.firstName, employee.lastName].filter(Boolean).join(' ') || employeeUuid;
            const existing = byEmployee.get(employeeUuid);
            if (existing) {
                existing.minutes += slot.minutes || 0;
            } else {
                const link = this.getBestRateLinkForEmployee(links, employeeUuid, this.currentSelectedDate);
                byEmployee.set(employeeUuid, {
                    name,
                    minutes: slot.minutes || 0,
                    rate: link?.pricePerHour ?? null,
                    employeeUuid,
                });
            }
        });

        byEmployee.forEach((entry) => {
            const hours = entry.minutes / 60;
            const budgetRate = this.getBudgetHourlyRate(entry.employeeUuid);
            const empCost = hours * budgetRate;
            totalCost += empCost;
            if (entry.rate != null && entry.rate > 0) {
                if (invoiceByDay) {
                    // Day-rate billing: days = hours / hoursPerDay, dayRate = pricePerHour × 8
                    const days = hours / hoursPerDay;
                    const dayRate = entry.rate * rateConversionHpd;
                    const rev = days * dayRate;
                    total += rev;
                    lines.push({
                        text: `${entry.name}: ${hours.toFixed(1)}u ÷ ${hoursPerDay}u/d = ${days.toFixed(2)}d × €${dayRate.toFixed(2)}/d (DB: €${
                            entry.rate
                        }/u × ${rateConversionHpd}) = €${rev.toFixed(2)}`,
                        rev,
                        cost: empCost,
                        margin: rev - empCost,
                        name: entry.name,
                    });
                } else {
                    const rev = hours * entry.rate;
                    total += rev;
                    lines.push({
                        text: `${entry.name}: ${hours.toFixed(1)}u × €${entry.rate}/u = €${rev.toFixed(2)}`,
                        rev,
                        cost: empCost,
                        margin: rev - empCost,
                        name: entry.name,
                    });
                }
            } else {
                lines.push({ text: `${entry.name}: ${hours.toFixed(1)}u × ⚠ geen tarief → €0`, rev: 0, cost: empCost, margin: -empCost, name: entry.name });
            }
        });

        return { total, totalCost, lines };
    }

    getDepartmentExpectedRevenue(deptKey: string): number {
        return (this.projectsForDepartment(deptKey) || []).reduce((sum, p) => sum + this.getExpectedRevenueForProject(p), 0);
    }

    getDepartmentTotalHoursMinutes(deptKey: string): number {
        return (this.projectsForDepartment(deptKey) || []).reduce((sum, p) => sum + this.getTotalWorkedMinutesForProject(p), 0);
    }

    /**
     * Calculate total cost for a department based on budget hourly rates.
     * For each employee in the department's projects: hours × budget hourly rate.
     */
    getDepartmentTotalCost(deptKey: string): number {
        const projects = this.projectsForDepartment(deptKey) || [];
        let totalCost = 0;
        projects.forEach((project) => {
            const slots = this.getTimeslotsForProject(project) || [];
            const byEmployee = new Map<string, { minutes: number; employeeUuid: string }>();
            slots.forEach((slot) => {
                const employeeUuid = slot?.timesheet?.employee?.uuid;
                if (!employeeUuid) return;
                const existing = byEmployee.get(employeeUuid);
                if (existing) {
                    existing.minutes += slot.minutes || 0;
                } else {
                    byEmployee.set(employeeUuid, { minutes: slot.minutes || 0, employeeUuid });
                }
            });
            byEmployee.forEach((entry) => {
                const hours = entry.minutes / 60;
                const rate = this.getBudgetHourlyRate(entry.employeeUuid);
                totalCost += hours * rate;
            });
        });
        return totalCost;
    }

    getDepartmentMargin(deptKey: string): number {
        const revenue = this.getDepartmentExpectedRevenue(deptKey);
        const cost = this.getDepartmentTotalCost(deptKey);
        // For cost departments (INTERN), revenue is already negative, so margin = revenue + cost would be wrong
        // For revenue departments: margin = revenue - cost
        if (this.isCostDepartment(deptKey)) {
            return -cost; // cost departments have no revenue, just show negative cost
        }
        return revenue - cost;
    }

    getDepartmentCostTooltip(deptKey: string): string {
        const projects = this.projectsForDepartment(deptKey) || [];
        const lines: string[] = [];
        const byEmployee = new Map<string, { name: string; minutes: number; employeeUuid: string }>();

        projects.forEach((project) => {
            const slots = this.getTimeslotsForProject(project) || [];
            slots.forEach((slot) => {
                const employee = slot?.timesheet?.employee;
                const employeeUuid = employee?.uuid;
                if (!employeeUuid) return;
                const name = [employee.firstName, employee.lastName].filter(Boolean).join(' ') || employeeUuid;
                const existing = byEmployee.get(employeeUuid);
                if (existing) {
                    existing.minutes += slot.minutes || 0;
                } else {
                    byEmployee.set(employeeUuid, { name, minutes: slot.minutes || 0, employeeUuid });
                }
            });
        });

        let total = 0;
        byEmployee.forEach((entry) => {
            const hours = entry.minutes / 60;
            const rate = this.getBudgetHourlyRate(entry.employeeUuid);
            const cost = hours * rate;
            total += cost;
            lines.push(`${entry.name}: ${hours.toFixed(1)}u × €${rate.toFixed(2)}/u = €${cost.toFixed(2)}`);
        });
        lines.push('─'.repeat(30));
        lines.push(`Totaal cost: €${total.toFixed(2)}`);
        return lines.join('\n');
    }

    isProjectInvoiceSent(project: any): boolean {
        const slots = this.getTimeslotsForProject(project) || [];
        return slots.some((slot) => this.isComanageInvoiceSent(slot));
    }
}
